import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import Row from "./Row";
import { getAllData, UpdateData } from "Services/PGXLis/LisSetting";
import { Loader } from "Shared/Common/Loader";
import NoRecord from "Shared/Common/NoRecord";
import useIsMobile from "Shared/hooks/useIsMobile";
import useLang from "Shared/hooks/useLanguage";
import usePagination from "Shared/hooks/usePagination";
import { ArrowDown, ArrowUp } from "Shared/Icons";
import CustomPagination from "Shared/JsxPagination";
import { StringRecord } from "Shared/Type";
import BreadCrumbs from "Utils/Common/Breadcrumb";
import { SortingTypeI } from "Utils/consts";

export interface IRows {
  labId: number;
  templateName: string;
  rowStatus: boolean | undefined;
  templateId: number;
  translatorId?: string;
  isActive?: boolean;
  labName?: string;
  translatorName?: string;
  cells?: any[];
  reqTypeId?: number;
}

interface SearchRequest {
  id: number;
  reqTypeId: number;
  actionDescription: string;
  actionIdentifier: string;
  isOn: boolean | null;
  isDeleted: boolean;
}

const INITIAL_SORTING_TYPE = {
  clickedIconData: "id",
  sortingOrder: "desc",
} as const;

const INITIAL_SEARCH_QUERY = {
  id: 0,
  actionDescription: "",
  actionIdentifier: "",
  isOn: null,
  isDeleted: false,
  reqTypeId: 11,
} as const;

const queryDisplayTagNames: StringRecord = {
  actionDescription: "Configuration",
} as const;

const REQ_TYPE_ID = 11;

export default function PGXLisSetting() {
  const { t } = useLang();
  const isMobile = useIsMobile();

  const searchRef = useRef<any>(null);
  const queryClient = useQueryClient();

  const [searchTrigger, setSearchTrigger] = useState(0);
  const [searchedTags, setSearchedTags] = useState<string[]>([]);
  const [sort, setSorting] = useState<SortingTypeI>(INITIAL_SORTING_TYPE);
  const [searchRequest, setSearchRequest] =
    useState<SearchRequest>(INITIAL_SEARCH_QUERY);

  // Pagination states
  const {
    curPage,
    pageSize,
    total,
    totalPages,
    pageNumbers,
    nextPage,
    prevPage,
    showPage,
    setPageSize,
    setTotal,
    setCurPage,
  } = usePagination();

  // Debounce timer ref
  const debounceTimer = useRef<any>(null);

  type InputChangeEvent = HTMLInputElement | HTMLSelectElement;
  const onInputChangeSearch = (e: React.ChangeEvent<InputChangeEvent>) => {
    setSearchRequest({ ...searchRequest, [e.target.name]: e.target.value });
  };
  const resetSearch = () => {
    setSearchRequest(INITIAL_SEARCH_QUERY);
    setSorting(INITIAL_SORTING_TYPE);
    setCurPage(1);
    setSearchTrigger((prev) => prev + 1);
  };

  const fetchGridData = async () => {
    const trimmedSearchRequest = Object.fromEntries(
      Object.entries(searchRequest).map(([key, value]) => [
        key,
        typeof value === "string" ? value.trim() : value,
      ])
    );

    const queryModel = {
      id: trimmedSearchRequest.id,
      actionDescription: trimmedSearchRequest.actionDescription,
      actionIdentifier: trimmedSearchRequest.actionIdentifier,
      isOn: trimmedSearchRequest.isOn,
      isDeleted: trimmedSearchRequest.isDeleted,
      reqTypeId: REQ_TYPE_ID,
    };

    const requestObj = {
      pageNumber: curPage,
      pageSize: pageSize,
      queryModel,
      sortColumn: sort.clickedIconData,
      sortDirection: sort.sortingOrder,
    };

    const res = await getAllData(requestObj);

    return {
      rows: res?.data?.data || [],
      total: res?.data?.total || 0,
    };
  };

  const pgxLisSettingsQueryKey = [
    "pgxLisSettings",
    curPage,
    pageSize,
    sort,
    searchTrigger,
  ] as const;

  const { data, isFetching, refetch } = useQuery(
    pgxLisSettingsQueryKey,
    fetchGridData,
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );

  const handleSort = (columnName: string) => {
    setSorting((prev) => ({
      clickedIconData: columnName,
      sortingOrder: prev.sortingOrder === "asc" ? "desc" : "asc",
    }));
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      setCurPage(1);
      setSearchTrigger((prev) => prev + 1);
    }
  };

  const handleTagRemoval = (clickedTag: string) => {
    setSearchRequest((prev) => ({
      ...prev,
      [clickedTag]: (INITIAL_SEARCH_QUERY as any)[clickedTag],
    }));
  };

  useEffect(() => {
    const activeTags = Object.entries(searchRequest)
      .filter(([, value]) => value)
      .map(([key]) => key);
    setSearchedTags(activeTags);
  }, [searchRequest]);

  useEffect(() => {
    if (searchedTags.length === 1) resetSearch();
  }, [searchedTags.length]);

  useEffect(() => {
    if (data?.total !== undefined) {
      setTotal(data.total);
    }
  }, [data]);

  const updateMutation = useMutation(UpdateData, {
    onMutate: async ({ id, status: checked }) => {
      await queryClient.cancelQueries("pgxLisSettings");

      const queryKey = pgxLisSettingsQueryKey;
      const previousData = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (oldData: any) => {
        if (!oldData?.rows) return oldData;
        return {
          ...oldData,
          rows: oldData.rows.map((x: any) =>
            x.id === id ? { ...x, isOn: checked } : x
          ),
        };
      });

      return { previousData, queryKey };
    },
    onError: (error, _variables, context) => {
      console.error("Failed to update status:", error);
      if (context?.previousData !== undefined && context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previousData);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries("pgxLisSettings");
    },
  });

  const handleIsActive = (id: number, checked: boolean) => {
    updateMutation.mutate({ id, status: checked });
  };

  return (
    <>
      <div className="app-toolbar py-2 py-lg-3">
        <div className="app-container container-fluid d-flex flex-wrap gap-4 justify-content-center justify-content-sm-between align-items-center">
          <BreadCrumbs />
        </div>
      </div>
      <div className="app-content flex-column-fluid">
        <div className="app-container container-fluid">
          <div className="card">
            <div className="card-body py-2">
              <div className="d-flex gap-4 flex-wrap mb-1">
                {searchedTags.map((tag) =>
                  tag === "isDeleted" || tag === "reqTypeId" ? null : (
                    <div
                      className="d-flex align-items-center cursor-pointer gap-1 p-2 rounded bg-light"
                      onClick={() => handleTagRemoval(tag)}
                      key={tag}
                    >
                      <span className="fw-bold">
                        {t(queryDisplayTagNames[tag])}
                      </span>
                      <i className="bi bi-x"></i>
                    </div>
                  )
                )}
              </div>
              <div className="d-flex flex-wrap gap-2 justify-content-center justify-content-sm-between align-items-center mb-2 col-12 responsive-flexed-actions">
                <div className="d-flex gap-2 responsive-flexed-actions">
                  <div className="d-flex align-items-center">
                    <span className="fw-400 mr-3">{t("Records")}</span>
                    <select
                      id={`IDResultDataPreconfTemplateSettingRecords`}
                      className="form-select w-125px h-33px rounded py-2"
                      data-kt-select2="true"
                      data-placeholder="Select option"
                      data-dropdown-parent="#kt_menu_63b2e70320b73"
                      data-allow-clear="true"
                      onChange={(e) => {
                        setPageSize(parseInt(e.target.value));
                      }}
                    >
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="50" selected>
                        50
                      </option>
                      <option value="100">100</option>
                    </select>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2 gap-lg-3">
                  <button
                    id={`IDResultDataPreconfTemplateSettingSearch`}
                    onClick={() => {
                      setCurPage(1);
                      refetch();
                    }}
                    className="btn btn-linkedin btn-sm fw-500 py-2 rounded-3"
                    aria-controls="Search"
                  >
                    {t("Search")}
                  </button>
                  <button
                    onClick={resetSearch}
                    type="button"
                    className="btn btn-secondary btn-sm btn-secondary--icon fw-bold py-2 rounded-3"
                    id={`IDResultDataPreconfTemplateSettingReset`}
                  >
                    <span>
                      <span>{t("Reset")}</span>
                    </span>
                  </button>
                </div>
              </div>
              <div className="card">
                <Box sx={{ height: "auto", width: "100%" }}>
                  <div className="table_bordered overflow-hidden">
                    <TableContainer
                      sx={
                        isMobile
                          ? {
                              overflowY: "hidden",
                            }
                          : {}
                      }
                      className="shadow-none"
                    >
                      <Table
                        aria-label="sticky table collapsible"
                        className="table table-cutome-expend table-bordered table-sticky-header table-head-2-bg table-bg table-head-custom table-vertical-center border-0 mb-0"
                      >
                        <TableHead>
                          <TableRow className="h-40px">
                            <TableCell>
                              <input
                                id={`IDResultDataPreconfTemplateSettingTemplateName`}
                                type="text"
                                name="actionDescription"
                                className="form-control bg-white rounded-2 fs-8 h-30px"
                                placeholder={t("Search ...")}
                                value={searchRequest.actionDescription}
                                onChange={onInputChangeSearch}
                                onKeyDown={handleKeyPress}
                              />
                            </TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                          <TableRow className="h-30px">
                            <TableCell
                              className="min-w-300px w-300px"
                              sx={{ width: "max-content" }}
                            >
                              <div
                                onClick={() => handleSort("actionDescription")}
                                className="d-flex justify-content-between cursor-pointer"
                                id=""
                                ref={searchRef}
                              >
                                <div style={{ width: "max-content" }}>
                                  {t("Configuration")}
                                </div>

                                <div className="d-flex justify-content-center align-items-center mx-4 mr-0">
                                  <ArrowUp
                                    CustomeClass={`${
                                      sort.sortingOrder === "desc" &&
                                      sort.clickedIconData ===
                                        "actionDescription"
                                        ? "text-success fs-7"
                                        : "text-gray-700 fs-7"
                                    }  p-0 m-0 "`}
                                  />
                                  <ArrowDown
                                    CustomeClass={`${
                                      sort.sortingOrder === "asc" &&
                                      sort.clickedIconData ===
                                        "actionDescription"
                                        ? "text-success fs-7"
                                        : "text-gray-700 fs-7"
                                    }  p-0 m-0`}
                                  />
                                </div>
                              </div>
                            </TableCell>
                            <TableCell
                              className="min-w-100px w-100px"
                              sx={{ width: "max-content" }}
                            >
                              <div className="d-flex justify-content-between">
                                <div style={{ width: "max-content" }}>
                                  {t("Inactive/Active")}
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {isFetching ? (
                            <TableCell colSpan={9}>
                              <Loader />
                            </TableCell>
                          ) : !data?.rows.length ? (
                            <NoRecord />
                          ) : (
                            data?.rows.map((item: any) => (
                              <Row
                                row={item}
                                onToggle={handleIsActive}
                                key={item.id}
                              />
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </div>
                </Box>
              </div>

              <CustomPagination
                curPage={curPage}
                nextPage={nextPage}
                pageNumbers={pageNumbers}
                pageSize={pageSize}
                prevPage={prevPage}
                showPage={showPage}
                total={total}
                totalPages={totalPages}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
